import { db } from '../firebase';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';

// ============================================================================
// APPLICATION / REFERRAL REVIEW SERVICE
// Applications are created by the submitApplication Cloud Function; referrals are
// created here (submitReferral). Both live in the `applications` collection with
// `creatorId` = the posting owner, so the creator (and admins) can read them.
// Firestore rules allow read/update when creatorId == auth.uid (or admin).
// ============================================================================

// Normalize Firestore Timestamp | {seconds} | Date | string → millis.
const toMs = (v) => {
  if (!v) return 0;
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  const d = new Date(v);
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

// All applications + referrals submitted to one posting (newest first).
// IMPORTANT: query by creatorId, NOT postingId. Firestore secures `applications`
// by creatorId == uid (or admin), and it rejects any query whose filters don't
// guarantee the result set is readable — so a postingId-only query throws
// permission-denied for the (non-admin) owner. Filtering by the posting's
// creatorId satisfies the rule for the owner; admins are allowed regardless.
export const getApplicationsForPosting = async (postingId, creatorId) => {
  if (!postingId || !creatorId) return [];
  const q = query(collection(db, 'applications'), where('creatorId', '==', creatorId));
  const snap = await getDocs(q);
  const rows = snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((a) => a.postingId === postingId);
  rows.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  return rows;
};

// All applications + referrals across everything a user has posted (newest first).
export const getApplicationsForCreator = async (creatorId) => {
  if (!creatorId) return [];
  const q = query(collection(db, 'applications'), where('creatorId', '==', creatorId));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  return rows;
};

// Everything a user has posted (jobs + projects), each with its applications
// attached — for the "My Postings" hub.
export const getMyPostings = async (uid) => {
  if (!uid) return { jobs: [], projects: [] };
  const [jobsSnap, projectsSnap, appsSnap] = await Promise.all([
    getDocs(query(collection(db, 'jobs'), where('postedBy', '==', uid))),
    getDocs(query(collection(db, 'projects'), where('postedBy', '==', uid))),
    getDocs(query(collection(db, 'applications'), where('creatorId', '==', uid))),
  ]);
  const apps = appsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const forPosting = (pid) =>
    apps.filter((a) => a.postingId === pid).sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  const jobs = jobsSnap.docs.map((d) => ({ id: d.id, ...d.data(), applications: forPosting(d.id) }));
  const projects = projectsSnap.docs.map((d) => ({ id: d.id, ...d.data(), applications: forPosting(d.id) }));
  return { jobs, projects };
};

// Creator/admin marks an application reviewed / shortlisted / declined, etc.
export const updateApplicationStatus = async (applicationId, status) => {
  await updateDoc(doc(db, 'applications', applicationId), {
    status,
    updatedAt: serverTimestamp(),
  });
};

// Persist a referral (the old form silently dropped these) + notify the creator.
export const submitReferral = async (posting, postingType, referrerUid, referral) => {
  const creatorId = posting?.postedBy || null;
  const appRef = await addDoc(collection(db, 'applications'), {
    kind: 'referral',
    postingId: posting.id,
    postingType,
    postingTitle: posting.title || '',
    creatorId,
    referrerId: referrerUid || null,
    refereeName: referral.name || '',
    refereeEmail: referral.email || '',
    relationship: referral.relationship || '',
    note: referral.note || '',
    status: 'new',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Notify the posting creator (rules allow any authed user to create a notification).
  if (creatorId) {
    await addDoc(collection(db, 'notifications'), {
      userId: creatorId,
      type: 'referral.submitted',
      title: `New Referral: ${posting.title || postingType}`,
      message: `${referral.name || 'Someone'} was referred for your ${postingType}`,
      data: { applicationId: appRef.id, postingId: posting.id, postingType, isReferral: true },
      read: false,
      createdAt: serverTimestamp(),
    });
  }

  return appRef.id;
};
